import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { notify } from "@/lib/notify";
import { Copy, Plus, Trash2 } from "lucide-react";

import { api } from "@/integrations/aws/resources";
import { SCHEDULE_STATE_LABEL, scheduleState } from "@/lib/fee-schedules";
import { useConfirm, useConfirmDelete } from "@/components/confirm-dialog";
import { PageHeader } from "@/components/app-shell";
import { useAuth } from "@/lib/auth";
import { ScheduleDialog, type DialogState } from "@/components/fee-schedules/schedule-dialog";
import { invalidateTargets } from "@/components/fee-schedules/attachment";
import { SCHEDULE_STATUS_TABS, type ScheduleStatus } from "@/components/fee-schedules/queries";
import type { FeeSchedule } from "@/components/fee-schedules/types";
import {
  BulkBar,
  RowCheckbox,
  SelectAllCheckbox,
  useRowSelection,
} from "@/components/bulk-actions";
import {
  ListPage,
  ListPageHeader,
  ListPageTable,
  ListSearch,
  ListToolbar,
  SortHeader,
  TableMessageRow,
  TablePagination,
  useDebounced,
  useListSort,
  usePagination,
} from "@/components/list-controls";

import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { RowAction, RowActions } from "@/components/row-actions";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Truncated } from "@/components/truncated";
import { ClientGroupSelect } from "@/components/entity-select";

export const Route = createFileRoute("/_authenticated/fee-schedules/")({
  head: () => ({
    meta: [
      { title: "Fee Schedules — Somasi Ops" },
      {
        name: "description",
        content: "Standard, client-group and company-specific fee schedules for Somasi services.",
      },
    ],
  }),
  component: FeeSchedulesPage,
});

function FeeSchedulesPage() {
  const { canEdit } = useAuth();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const confirmDelete = useConfirmDelete();
  const navigate = useNavigate();

  const [dialog, setDialog] = useState<DialogState | null>(null);
  /**
   * Which schedules the table is showing.
   *
   * Active is the default because it is the only tab that answers "what are we
   * billing?" — the inactive ones are last year's rates and next year's drafts,
   * which matter when you go looking for them and are noise the rest of the
   * time.
   */
  const [status, setStatus] = useState<ScheduleStatus>("active");
  const [groupFilter, setGroupFilter] = useState("all");
  const [search, setSearch] = useState("");
  // Held back so a search is one request when typing stops, not one per key.
  const query = useDebounced(search.trim());

  // A group names the one schedule it reads, which is what makes filtering by
  // group a single-id lookup. Only the chosen group is read, and only once one
  // is chosen — the filter's own list is `ClientGroupSelect`'s business, and it
  // searches the API rather than holding the table.
  const chosenGroup = useQuery({
    queryKey: ["client_group_schedule", groupFilter],
    enabled: groupFilter !== "all",
    queryFn: async () => {
      const { data, error } = await api.clientGroups.get(groupFilter, ["id", "fee_schedule_id"]);
      if (error) throw error;
      return (data ?? null) as { id: string; fee_schedule_id: string | null } | null;
    },
  });

  // `undefined` while that single row is in flight: the schedule the group reads
  // is not known yet, and guessing `""` here would blank the table for a beat on
  // every pick.
  const groupPending = groupFilter !== "all" && chosenGroup.data === undefined;
  const filteredScheduleId =
    groupFilter === "all"
      ? null
      : groupPending
        ? undefined
        : (chosenGroup.data?.fee_schedule_id ?? "");

  // Switching tab is a new filter, so it starts again at page 1 — page 3 of the
  // active schedules says nothing about where to be in the inactive ones.
  const sort = useListSort({ column: "name", ascending: true });
  const pager = usePagination({ resetKey: [status, groupFilter, query, sort.sort] });

  // The filter, the order and the page are the API's: `GET
  // /fee-schedules?active=eq.true`. Switching tab refetches rather than hiding
  // rows the browser already holds, so the count in the tab and the rows under
  // it cannot disagree. Written out here rather than through `feeSchedulesQuery`
  // because that helper orders by name only; the key keeps its `fee_schedules`
  // prefix so `refresh` still reaches it.
  const { data: result, isLoading } = useQuery({
    queryKey: [
      "fee_schedules",
      status,
      filteredScheduleId ?? null,
      { query, page: pager.page, pageSize: pager.pageSize, sort: sort.sort },
    ],
    queryFn: async () => {
      const list = api.feeSchedules
        .list()
        .order(sort.sort.column, { ascending: sort.sort.ascending });
      if (sort.sort.column !== "name") list.order("name");
      if (status !== "all") list.eq("active", status === "active");
      if (query) {
        list.or([
          { column: "name", op: "ilike", value: `%${query}%` },
          { column: "notes", op: "ilike", value: `%${query}%` },
        ]);
      }
      // A group reads exactly one schedule; `""` is a group that reads none, which
      // must match nothing rather than everything.
      if (filteredScheduleId !== undefined && filteredScheduleId !== null) {
        list.eq("id", filteredScheduleId || "__none");
      }
      const { data, error, count } = await list.page(pager.page, pager.pageSize);
      if (error) throw error;
      return { rows: (data ?? []) as FeeSchedule[], total: count ?? 0 };
    },
    // Held until the chosen group's schedule is known, so the unfiltered page is
    // never fetched and shown on the way to the filtered one.
    enabled: !groupPending,
    placeholderData: (previous) => previous,
  });

  const schedules = result?.rows ?? [];
  const total = result?.total;

  const counts = useQuery({
    queryKey: ["fee_schedules_count"],
    queryFn: async () => {
      // `head=true&count=exact`: the totals without the rows, so the tab labels
      // cost two short requests rather than a second copy of the table.
      const [active, inactive] = await Promise.all([
        api.feeSchedules.list().eq("active", true).countOnly(),
        api.feeSchedules.list().eq("active", false).countOnly(),
      ]);
      return {
        active: active.count ?? 0,
        inactive: inactive.count ?? 0,
        all: (active.count ?? 0) + (inactive.count ?? 0),
      };
    },
  });

  const sel = useRowSelection(schedules);

  /** Every status tab, plus the counts beside them. */
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["fee_schedules"] });
    qc.invalidateQueries({ queryKey: ["fee_schedules_count"] });
  };

  /** Each schedule has its own URL, so a price list can be linked to and reloaded. */
  const openSchedule = (id: string) =>
    navigate({ to: "/fee-schedules/$scheduleId", params: { scheduleId: id } });

  /**
   * The switch in the Status column: one `PATCH` of one column, the same act the
   * schedule's own page offers on its Status tile.
   *
   * It asks before switching one off because the flag reads as more than it is:
   * nothing stops being billed. Every company and group pointing at the schedule
   * goes on being charged these rates until it is detached from the company or its
   * client group, so the question says that rather than letting the switch imply it.
   *
   * No optimistic patch, unlike the other lists with this switch: the tabs filter on
   * this very column, so a row switched off on the Active tab is leaving the list
   * rather than changing in place, and the refetch is what moves it and corrects the
   * counts in the tab labels.
   */
  const setActive = async (row: FeeSchedule, active: boolean) => {
    if (
      !active &&
      !(await confirm({
        title: `Mark “${row.name}” inactive?`,
        description:
          "This marks the schedule as out of use. It does not change any fee: companies and groups pointing at it go on being billed these rates. To stop that, detach the schedule on the company or its client group.",
        confirmLabel: "Mark inactive",
        tone: "warning",
      }))
    ) {
      return;
    }

    const { error } = await api.feeSchedules.update(row.id, { active });
    if (error) return notify.error(error);

    notify.success(`“${row.name}” is now ${active ? "active" : "inactive"}`);
    refresh();
  };

  const remove = async (row: FeeSchedule) => {
    if (
      !(await confirmDelete(
        `schedule “${row.name}”`,
        "Its whole price history goes with it, and any company or group pointing at it falls back to the level below.",
      ))
    )
      return;
    // One request. `DELETE /fee-schedules/{id}` also removes the schedule's
    // price lines and clears the pointer on whichever group or company named it
    // — cascades the browser used to attempt itself, in whatever order it got
    // to them, leaving orphans behind whenever one of the calls failed.
    const { error } = await api.feeSchedules.remove(row.id);
    if (error) return notify.error(error);

    notify.success("Schedule deleted");
    refresh();
    invalidateTargets(qc);
  };

  // Name, client groups, notes, status — plus the checkbox and actions for an editor.
  const colSpan = canEdit ? 6 : 4;

  const empty =
    groupFilter !== "all" || query
      ? "No fee schedules match these filters."
      : status === "active"
        ? "No active fee schedules. Create one, or look under Inactive for a draft."
        : status === "inactive"
          ? "No inactive fee schedules — every one is active."
          : "No fee schedules yet.";

  return (
    <ListPage>
      <ListPageHeader>
        <PageHeader
          title="Fee Schedules"
          description="Priced lists of services for everyone, one client group or one company — one active per target."
          actions={
            canEdit && (
              <Button onClick={() => setDialog({ mode: "create", source: null })}>
                <Plus className="mr-2 h-4 w-4" /> New schedule
              </Button>
            )
          }
        />

        <Tabs
          value={status}
          onValueChange={(v) => {
            setStatus(v as ScheduleStatus);
            sel.clear();
          }}
          className="mb-3 flex flex-row flex-wrap items-center gap-2"
        >
          <TabsList className="shrink-0">
            {SCHEDULE_STATUS_TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
                {counts.data && (
                  <span className="ml-2 text-xs tabular-nums opacity-70">
                    {counts.data[tab.value]}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
          <ListSearch value={search} onChange={setSearch} placeholder="Search name or notes…" />
        </Tabs>

        <ListToolbar>
          <ClientGroupSelect
            value={groupFilter === "all" ? "" : groupFilter}
            onChange={(id) => {
              setGroupFilter(id || "all");
              sel.clear();
            }}
            placeholder="All client groups"
            clearable
            clearLabel="All client groups"
            className="w-64"
            contentClassName="w-80 p-0"
          />
          {groupFilter !== "all" && filteredScheduleId === "" && (
            <span className="text-xs text-muted-foreground">
              That group reads no fee schedule, so it is billed the standard fee.
            </span>
          )}
        </ListToolbar>

        {canEdit && (
          <BulkBar
            sel={sel}
            resource={api.feeSchedules}
            entityLabel="fee schedule"
            onDone={refresh}
          />
        )}
      </ListPageHeader>

      <ListPageTable>
        <Table containerClassName="min-h-0 flex-1">
          <TableHeader sticky>
            <TableRow>
              {canEdit && (
                <TableHead className="w-10">
                  <SelectAllCheckbox sel={sel} />
                </TableHead>
              )}
              <SortHeader sort={sort} column="name">
                Name
              </SortHeader>
              {/* The schedule's copy of its groups' names — a list, not a column to order by. */}
              <TableHead>Client groups</TableHead>
              <SortHeader sort={sort} column="notes">
                Notes
              </SortHeader>
              <SortHeader sort={sort} column="active">
                Status
              </SortHeader>
              {/* Every row action is an edit, so a reader would get an empty column. */}
              {canEdit && (
                <TableHead className="w-px whitespace-nowrap text-right">Actions</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableMessageRow colSpan={colSpan}>Loading…</TableMessageRow>
            ) : schedules.length === 0 ? (
              <TableMessageRow colSpan={colSpan}>{empty}</TableMessageRow>
            ) : (
              schedules.map((s) => (
                <TableRow
                  key={s.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => openSchedule(s.id)}
                >
                  {canEdit && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <RowCheckbox sel={sel} id={s.id} />
                    </TableCell>
                  )}
                  <TableCell className="font-medium">
                    {/* Underlined because the whole row opens the schedule. */}
                    <span className="underline underline-offset-2">{s.name}</span>
                    {/*
                      Marked here as well as on its own page: which list the service
                      catalogue reads is a fact about this table, and finding it by
                      opening schedules one at a time is the search this saves.
                    */}
                    {s.is_standard && (
                      <Badge variant="secondary" className="ml-2 font-normal">
                        Standard
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {/*
                      Rendered from the schedule's own copy of the names, so a page of
                      schedules costs one request rather than a lookup per row.
                    */}
                    {s.client_groups?.length ? (
                      <div className="flex flex-wrap gap-1">
                        {s.client_groups.map((group) => (
                          <Badge key={group.id} variant="secondary" className="font-normal">
                            {group.name}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="max-w-xs text-xs text-muted-foreground">
                    <Truncated text={s.notes} />
                  </TableCell>
                  {/* Stops the click here: the rest of the row opens the schedule. */}
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={s.active}
                        disabled={!canEdit}
                        aria-label={`Switch ${s.name} ${s.active ? "off" : "on"}`}
                        onCheckedChange={(value) => setActive(s, value)}
                      />
                      <span className="text-xs text-muted-foreground">
                        {SCHEDULE_STATE_LABEL[scheduleState(s)]}
                      </span>
                    </div>
                  </TableCell>
                  {canEdit && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <RowActions>
                        <RowAction
                          label="Copy this schedule"
                          onClick={() => setDialog({ mode: "copy", source: s })}
                        >
                          <Copy />
                        </RowAction>
                        <RowAction label="Delete" destructive onClick={() => remove(s)}>
                          <Trash2 />
                        </RowAction>
                      </RowActions>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination pagination={pager} total={total} loading={isLoading} unit="schedule" />
      </ListPageTable>

      <ScheduleDialog
        state={dialog}
        onClose={() => setDialog(null)}
        onSaved={refresh}
        onCreated={openSchedule}
      />
    </ListPage>
  );
}
